import { useCallback, useRef, useState } from 'react';
import {
  confirmCancelChanges,
  confirmClearSelection,
} from '../utils/advancedSelectConfirmDialogs.ts';

export interface IUseAdvancedSelectModalOptions {
  confirmCancel?: () => Promise<boolean>;
  confirmClear?: () => Promise<boolean>;
}

/**
 * Hook per gestire lo stato del modale di selezione avanzata,
 * con snapshot dei valori per poter annullare le modifiche
 */
export function useAdvancedSelectModal<T>(
  value: T,
  onRestore: (val: T) => void,
  onClear: () => void,
  options?: IUseAdvancedSelectModalOptions,
) {
  const confirmCancel = options?.confirmCancel ?? confirmCancelChanges;
  const confirmClear = options?.confirmClear ?? confirmClearSelection;

  const [open, setOpen] = useState(false);
  const snapshotRef = useRef<T>(value);

  const handleOpen = useCallback(() => {
    snapshotRef.current = value;
    setOpen(true);
  }, [value]);

  const handleConfirm = useCallback(() => {
    setOpen(false);
  }, []);

  const handleCancel = useCallback(async () => {
    const confirmed = await confirmCancel();
    if (!confirmed) return;

    onRestore(snapshotRef.current);
    setOpen(false);
  }, [onRestore, confirmCancel]);

  const handleClearSelection = useCallback(async () => {
    const confirmed = await confirmClear();
    if (!confirmed) return;

    onClear();
  }, [onClear, confirmClear]);

  return {
    open,
    setOpen,
    snapshotRef,
    handleOpen,
    handleConfirm,
    handleCancel,
    handleClearSelection,
  };
}
